import { Anchor, Badge, Group, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { Link, useSearchParams } from 'react-router';
import { datum } from '../oblika/oblika';
import { Kartica } from '../postavitev/Podatki';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { CLANKI, isci, KATEGORIJE, novosti, type Clanek } from './clanki';

/**
 * Pomoč – navodila (članki iz {@code src/pomoc/clanki/}) z iskanjem (v URL-ju), novosti in kategorije ter kontakt
 * podpore. {@code osnova}: {@code /pomoc} v aplikaciji ali {@code /navodila} javno (brez kontakta).
 */
export function Pomoc() {
  return (
    <ZaslonSeznama>
      <GlavaZaslona naslov="Pomoč" opis="Navodila po korakih za delo v programu in novosti." />
      <SeznamNavodil osnova="/pomoc" />
      <Text size="sm" c="dimmed" mt="md" maw={1000}>
        Niste našli odgovora? Pišite na{' '}
        <Anchor href="mailto:podpora@lunalabs.solutions" size="sm">
          podpora@lunalabs.solutions
        </Anchor>{' '}
        ali uporabite{' '}
        <Anchor component={Link} to="/povratne-informacije" size="sm">
          Povratne informacije
        </Anchor>
        .
      </Text>
    </ZaslonSeznama>
  );
}

/** Iskanje, novosti in članki po kategorijah (v aplikaciji in javno). */
export function SeznamNavodil({ osnova }: { osnova: string }) {
  const [parametri, setParametri] = useSearchParams();
  const niz = parametri.get('iskanje') ?? '';
  const zadetki = isci(CLANKI, niz);
  const nove = novosti(CLANKI).slice(0, 5);
  return (
    <Stack gap="md" maw={1000}>
      <TextInput
        aria-label="Iskanje po navodilih"
        placeholder="Iskanje po navodilih, npr. leasing, podpis, stroški"
        leftSection={<IconSearch size={16} />}
        value={niz}
        onChange={(e) => {
          const v = e.currentTarget.value;
          setParametri(v ? { iskanje: v } : {}, { replace: true });
        }}
      />
      {niz ? (
        <Kartica naslov={`Zadetki (${zadetki.length})`}>
          {zadetki.length === 0 ? (
            <Text size="sm" c="dimmed">
              Ni navodil za »{niz}«. Poskusite z drugo besedo ali nam pišite prek »Povratne informacije«.
            </Text>
          ) : (
            <SeznamClankov clanki={zadetki} osnova={osnova} />
          )}
        </Kartica>
      ) : (
        <>
          {nove.length > 0 && (
            <Kartica naslov="Novosti">
              <SeznamClankov clanki={nove} osnova={osnova} zDatumom />
            </Kartica>
          )}
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {[...KATEGORIJE, ...new Set(CLANKI.map((c) => c.kategorija).filter((k) => !KATEGORIJE.includes(k)))]
              .map((k) => [k, CLANKI.filter((c) => c.kategorija === k)] as const)
              .filter(([, clanki]) => clanki.length > 0)
              .map(([k, clanki]) => (
                <Kartica key={k} naslov={k}>
                  <SeznamClankov clanki={clanki} osnova={osnova} />
                </Kartica>
              ))}
          </SimpleGrid>
        </>
      )}
    </Stack>
  );
}

function SeznamClankov({ clanki, osnova, zDatumom }: { clanki: Clanek[]; osnova: string; zDatumom?: boolean }) {
  return (
    <Stack gap="sm">
      {clanki.map((c) => (
        <div key={c.id}>
          <Group gap={8} wrap="nowrap" align="baseline">
            <Anchor component={Link} to={`${osnova}/${c.id}`} fw={600}>
              {c.naslov}
            </Anchor>
            {zDatumom && c.novo && (
              <Badge size="xs" variant="light" style={{ flexShrink: 0 }}>
                {datum(c.novo)}
              </Badge>
            )}
          </Group>
          {c.opis && (
            <Text size="sm" c="dimmed">
              {c.opis}
            </Text>
          )}
        </div>
      ))}
    </Stack>
  );
}
